import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { AnimatePresence, m } from 'motion/react';
import { cx } from '../../lib/format';
import { Button } from './Button';

export function PageHeader({ title, subtitle, actions, eyebrow }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode; eyebrow?: ReactNode }) {
  return (
    <div className="page-heading mb-6 flex flex-wrap items-end justify-between gap-x-6 gap-y-4 sm:mb-7">
      <div className="min-w-0 max-w-[720px]">
        {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
        <h1 className="title-large break-words">{title}</h1>
        {subtitle && <p className="mt-1.5 text-[14px] leading-relaxed text-ink-2">{subtitle}</p>}
      </div>
      {actions && <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto [&>button]:max-sm:flex-1">{actions}</div>}
    </div>
  );
}

export function Card({ children, className, padded = true }: { children: ReactNode; className?: string; padded?: boolean }) {
  return <section className={cx('card min-w-0', padded && 'p-5', className)}>{children}</section>;
}

export function CardHeader({ title, subtitle, action, className }: { title: ReactNode; subtitle?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cx('flex items-start justify-between gap-3', className)}>
      <div className="min-w-0">
        <h2 className="headline">{title}</h2>
        {subtitle && <p className="mt-0.5 text-[12.5px] text-ink-3">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

/** Indicador numérico. "hint" explica de onde vem o número (previsto, realizado, saldo). */
export function Stat({ label, value, hint, tone, onClick, icon, active }: { label: ReactNode; value: ReactNode; hint?: ReactNode; tone?: 'default' | 'positive' | 'negative' | 'warning'; onClick?: () => void; icon?: ReactNode; active?: boolean }) {
  const toneClass = { default: 'text-ink', positive: 'text-success', negative: 'text-danger', warning: 'text-warning' }[tone ?? 'default'];
  const Comp = onClick ? 'button' : 'div';
  return (
    <Comp
      onClick={onClick}
      className={cx(
        'card stat-card group flex min-w-0 flex-col items-start p-4 text-left transition-[box-shadow,transform] duration-200',
        onClick && 'cursor-pointer hover:shadow-md active:scale-[0.99]',
        active && 'ring-2 ring-primary',
      )}
    >
      <div className="flex w-full items-center justify-between gap-2">
        <span className="text-[12.5px] font-[560] text-ink-2">{label}</span>
        {icon && <span className="text-ink-3 [&_svg]:size-4" aria-hidden>{icon}</span>}
      </div>
      <div className={cx('num mt-2 max-w-full truncate text-[19px] font-[650] tracking-[-0.022em] sm:text-[22px]', toneClass)} title={typeof value === 'string' ? value : undefined}>{value}</div>
      {hint && <div className="mt-1 text-[12px] text-ink-3">{hint}</div>}
    </Comp>
  );
}

export function Pagination({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return total > 0 ? <div className="px-4 py-3 text-[12.5px] text-ink-3">{total} {total === 1 ? 'registro' : 'registros'}</div> : null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 text-[12.5px] text-ink-3">
      <span className="num">
        {from}–{to} de {total}
      </span>
      <div className="flex items-center gap-1">
        <Button size="sm" variant="ghost" aria-label="Página anterior" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          <ChevronLeft className="size-4" />
        </Button>
        <span className="num px-1">
          {page} / {pages}
        </span>
        <Button size="sm" variant="ghost" aria-label="Próxima página" disabled={page >= pages} onClick={() => onPage(page + 1)}>
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  );
}

/** Tabela com cabeçalho fixo e leitura confortável; no celular a página usa listas em cartão. */
export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx('overflow-x-auto', className)}>
      <table className="w-full border-separate border-spacing-0 text-[13.5px]">{children}</table>
    </div>
  );
}

export function Th({ children, className, align = 'left' }: { children?: ReactNode; className?: string; align?: 'left' | 'right' | 'center' }) {
  return (
    <th scope="col" className={cx('sticky top-0 z-[1] whitespace-nowrap border-b border-separator bg-surface px-3 py-3 text-[12px] font-[560] text-ink-3 first:pl-5 last:pr-5', align === 'right' && 'text-right', align === 'center' && 'text-center', className)}>
      {children}
    </th>
  );
}

export function Td({ children, className, align = 'left' }: { children?: ReactNode; className?: string; align?: 'left' | 'right' | 'center' }) {
  return <td className={cx('border-b border-separator px-3 py-3 align-middle first:pl-5 last:pr-5 group-last:border-b-0', align === 'right' && 'num text-right', align === 'center' && 'text-center', className)}>{children}</td>;
}

export function useMediaQuery(query: string): boolean {
  const [match, setMatch] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatch(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return match;
}

/** Menu suspenso simples (popover) com fechamento por clique fora e Esc. */
export function Popover({ trigger, children, align = 'left', className }: { trigger: (p: { open: boolean; toggle: () => void }) => ReactNode; children: (close: () => void) => ReactNode; align?: 'left' | 'right'; className?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <div ref={ref} className="relative">
      {trigger({ open, toggle: () => setOpen((v) => !v) })}
      <AnimatePresence>
        {open && (
          <m.div
            initial={{ opacity: 0, scale: 0.97, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.98, y: -2 }}
            transition={{ duration: 0.16, ease: [0.2, 0.8, 0.2, 1] }}
            style={{ transformOrigin: align === 'right' ? 'top right' : 'top left' }}
            className={cx('glass-strong absolute z-40 mt-2 min-w-[220px] rounded-[18px] p-1.5', align === 'right' ? 'right-0' : 'left-0', className)}
            role="menu"
          >
            {children(() => setOpen(false))}
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function MenuItem({ children, onClick, icon, active, danger }: { children: ReactNode; onClick: () => void; icon?: ReactNode; active?: boolean; danger?: boolean }) {
  return (
    <button role="menuitem" type="button" onClick={onClick} className={cx('flex min-h-10 w-full items-center gap-2.5 rounded-[12px] px-2.5 py-2 text-left text-[13.5px] transition-colors hover:bg-hover coarse:min-h-11', active && 'bg-primary-soft text-primary-text', danger && 'text-danger')}>
      {icon && <span className={cx('shrink-0 [&_svg]:size-[18px]', active ? 'text-primary-text' : 'text-ink-3')} aria-hidden>{icon}</span>}
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </button>
  );
}

export function DefinitionList({ items, className }: { items: Array<{ label: ReactNode; value: ReactNode; full?: boolean }>; className?: string }) {
  return (
    <dl className={cx('grid grid-cols-2 gap-x-4 gap-y-3.5', className)}>
      {items.map((it, i) => (
        <div key={i} className={cx('min-w-0', it.full && 'col-span-2')}>
          <dt className="text-[12px] text-ink-3">{it.label}</dt>
          <dd className="mt-0.5 break-words text-[14px] text-ink">{it.value}</dd>
        </div>
      ))}
    </dl>
  );
}
